<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchDocument,
  createDocument,
  updateDocument,
  fetchDocumentLangs,
  fetchDocumentLang,
  createDocumentLang,
  deleteDocumentLang,
} from '@apis/admin/contract'
import { fetchConfig } from '@apis/admin/config'
import LangItem from '@components/admin/lang-input'
import ContractDocumentLangFileUpload from '@components/admin/contract-document-lang-file-upload'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'
import find from 'lodash/find'
import { getToken } from '@utils/auth'

export default {
  components: {
    LangItem,
    ContractDocumentLangFileUpload,
  },
  props: {
    document_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      config_service: null,
      loading: false,
      active_tab: 'info',
      document_langs: [],
      lang_params: null,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      this.loadConfigService()
        .then(() => Promise.all([this.loadDocument()]))
        .then(() => this.loadDocumentLangs())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadConfigService() {
      const { config } = await fetchConfig('service')
      this.config_service = config
    },
    async loadDocument() {
      const { document_info } = await fetchDocument(this.document_seq)
      this.params = defaults(document_info, {
        seq: null,
        name: '',
        title: '',
        document_type: 'TEXT',
        master_locale: this.config_service.default_locale,
        description: '',
      })
      this.info = document_info
    },
    async loadDocumentLangs() {
      if (!this.info) return []

      const { data } = await fetchDocumentLangs(this.info.seq)

      this.document_langs = data
    },
    async loadDocumentLang(lang_seq) {
      const { data } = await fetchDocumentLang(this.info.seq, lang_seq)

      this.lang_params = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateDocument : createDocument
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_document_modify')
        : this.$t('admin.msg_document_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddLang() {
      this.document_langs.push({
        document_seq: this.params.seq,
        locale: null,
        content: '',
        file_token: null,
      })
    },
    async handleDeleteLang(idx) {
      this.loading = true
      try {
        if (this.document_langs[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteDocumentLang(this.info.seq, this.document_langs[idx].seq)
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.document_langs.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitLang(idx) {
      this.loading = true
      const params = this.document_langs[idx]
      createDocumentLang(params)
        .then(({ seq }) => fetchDocumentLang(this.info.seq, seq))
        .then(({ lang_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.document_langs, idx, { ...lang_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
    find,
    getToken,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('contract_document_info.document_type') }}</div></th
              >
              <td>
                <template v-if="params.seq">{{
                  fromPairs(constants.contract.document.document_type)[
                    params.document_type
                  ]
                }}</template>
                <select v-else v-model="params.document_type">
                  <option
                    v-for="val in [
                      ...constants.contract.document.document_type,
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </td>
              <th scope="row"
                ><div>{{ $t('contract_document_info.main_locale') }}</div></th
              >
              <td>
                <template v-if="params.seq"
                  >{{
                    fromPairs(constants.lang.locales)[params.master_locale]
                  }}
                  [{{ params.master_locale }}]</template
                >
                <select v-else v-model="params.master_locale">
                  <option
                    v-for="support_locale in (config_service || [])
                      .support_locales"
                    :key="support_locale"
                    :value="support_locale"
                    >{{
                      fromPairs(constants.lang.locales)[support_locale]
                    }}</option
                  >
                </select>
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('contract_document_info.name') }}</div></th
              >
              <td>
                <template v-if="params.seq">{{ params.name }}</template>
                <input
                  v-else
                  v-model="params.name"
                  type="text"
                  :class="[$style.inputTypeText]"
                />
              </td>
              <th scope="row"
                ><div>{{ $t('contract_document_info.title') }}</div></th
              >
              <td
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('contract_document_info.info') }}</div></th
              >
              <td colspan="3"
                ><LangItem
                  v-if="!loading"
                  v-model="params.description"
                ></LangItem
              ></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!params || !params.seq"
      :label="$t('contract_document_info.contract_language')"
      name="lang"
    >
      <div
        v-if="!find(document_langs, { locale: params.master_locale })"
        :class="[$style.infoText]"
      >
        <p :class="[$style.warning]"
          >{{ $t('contract_document_info.master_locale') }}'{{
            fromPairs(constants.lang.locales)[params.master_locale]
          }}'{{ $t('contract_document_info.msg_master_locale') }}</p
        >
      </div>
      <table
        v-if="params && params.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">
              <div :style="[$style.nowrap]">
                {{ $t('contract_document_info.locale') }}
              </div>
            </th>
            <th scope="col">
              <div :style="[$style.nowrap]">
                {{ $t('contract_document_info.document_type') }}
              </div>
            </th>
            <th scope="col">
              <div>
                {{ $t('contract_document_info.content_write') }}
              </div>
            </th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddLang"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(document_lang, idx) in document_langs"
            :key="document_lang.seq ? `${document_lang.seq}` : `create-${idx}`"
          >
            <td :class="[$style.tCenter, $style.nowrap]">
              <select
                v-model="document_lang.locale"
                :disabled="document_lang.seq"
              >
                <option v-if="!document_lang.locale" :value="null">{{
                  $t('contract_document_info.select')
                }}</option>
                <option
                  v-for="support_locale in (config_service || [])
                    .support_locales"
                  :key="support_locale"
                  :value="support_locale"
                  >{{
                    fromPairs(constants.lang.locales)[support_locale]
                  }}</option
                >
              </select>
            </td>
            <td :class="[$style.tCenter, $style.nowrap]">
              {{
                fromPairs(constants.contract.document.document_type)[
                  params.document_type
                ]
              }}
            </td>
            <td :class="[$style.tCenter, $style.wide]">
              <quill-editor
                v-if="params.document_type === 'TEXT'"
                v-model="document_lang.content"
                :disabled="document_lang.seq"
                style="min-height:100px;"
              />
              <textarea
                v-if="0"
                v-model="document_lang.content"
                :disabled="document_lang.seq"
                :class="[$style.inputTypeTextArea, $style.fullWidth]"
              ></textarea>
              <template v-if="params.document_type === 'FILE'">
                <a
                  v-if="document_lang.seq"
                  :href="
                    `/api/v1/admin/file/${
                      document_lang.file_seq
                    }/download?access_token=${getToken()}`
                  "
                  >{{ document_lang.source_filename }}</a
                >
                <ContractDocumentLangFileUpload
                  v-else
                  v-model="document_lang.file_token"
                  :document_seq="params.seq"
                ></ContractDocumentLangFileUpload>
              </template>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="!document_lang.seq"
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitLang(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteLang(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!document_langs.length">
            <td :class="$style.tCenter" colspan="4">{{
              $t('contract_document_info.msg_contract_document_register_no')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
